import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  useAxiosInstance,
} from 'shared/hooks/utils';
import type { z } from 'zod/v4';
import { CreateExternalMailboxConfigurationVariablesSchema } from './use-create-external-mailbox-configuration';
import {
  EXTERNAL_MAILBOX_CONFIGURATIONS_QUERY_KEY,
  EXTERNAL_MAILBOX_CONFIGURATIONS_URL,
} from './use-load-external-mailbox-configurations';

// Every field is optional: omitting `password` is how the backend is told to keep
// the stored one, and a Microsoft mailbox only ever sends its signature.
const UpdateExternalMailboxConfigurationBodySchema =
  CreateExternalMailboxConfigurationVariablesSchema.partial();

export type UpdateExternalMailboxConfigurationBody = z.infer<
  typeof UpdateExternalMailboxConfigurationBodySchema
>;

export type UpdateExternalMailboxConfigurationVariables = {
  id: number;
  data: UpdateExternalMailboxConfigurationBody;
};

export async function updateExternalMailboxConfigurationFn(
  axiosInstance: Promise<AxiosInstance>,
  { id, data }: UpdateExternalMailboxConfigurationVariables,
) {
  const instance = await axiosInstance;

  return instance.put(
    `${EXTERNAL_MAILBOX_CONFIGURATIONS_URL}/${id}`,
    UpdateExternalMailboxConfigurationBodySchema.parse(data),
  );
}

export function useUpdateExternalMailboxConfiguration() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, UpdateExternalMailboxConfigurationVariables>({
    mutationFn: variables => updateExternalMailboxConfigurationFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: EXTERNAL_MAILBOX_CONFIGURATIONS_QUERY_KEY });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    UpdateExternalMailboxConfigurationVariables,
    'updateExternalMailboxConfiguration'
  >('updateExternalMailboxConfiguration', mutation);
}
